import { useEffect, useState, useRef } from "react";
import { api } from "../../api";
import { useSubView } from "../../App";
import { Btn, Card, Badge, Field, Input, Select, ProgressBar, PageHead, Avatar } from "../../components/ui";
import Icon from "../../components/Icon";
import { BRAND, BRAND_SOFT, BRAND_DARK, BRAND_TINT } from "../../theme";

function RowMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 160) });
    }
    setOpen((o) => !o);
  };
  // Rendered with position:fixed so the menu is never clipped by the table's
  // overflow containers (this was the "can't select student actions" bug).
  return (
    <>
      <button ref={btnRef} onClick={toggle} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500">⋮</button>
      {open && (<>
        <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
        <div className="fixed z-50 w-40 rounded-lg border border-slate-200 bg-white shadow-lg py-1" style={{ top: pos.top, left: pos.left }}>
          {items.map((it) => <button key={it.label} onClick={() => { it.onClick(); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">{it.label}</button>)}
        </div>
      </>)}
    </>
  );
}

function StatusModal({ student, onClose, onSaved }) {
  if (!student) return null;
  const opts = ["Active", "Completed", "On Hold", "Discontinued"];
  const pick = async (st) => { await api.setStatus(student.id, st); onSaved(); onClose(); };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-semibold text-slate-900">Change status — {student.full_name}</h2><button onClick={onClose} className="text-slate-400"><Icon name="x" size={18} /></button></div>
        <div className="space-y-2">{opts.map((o) => (
          <button key={o} onClick={() => pick(o)} className="w-full flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50">
            <Badge>{o}</Badge>{student.status === o && <span style={{ color: BRAND }}><Icon name="check" size={16} /></span>}
          </button>
        ))}</div>
      </Card>
    </div>
  );
}

function AssignmentActions({ id, onDone, toast }) {
  const [fb, setFb] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try { await api.reviewAssignment(id, action, fb); toast(action === "approve" ? "Assignment approved" : "Assignment rejected"); onDone(); }
    catch (e) { toast(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2">
      <textarea value={fb} onChange={(e) => setFb(e.target.value)} placeholder="Feedback / notes (optional)" rows={2} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
      <div className="flex gap-2">
        <button disabled={busy} onClick={() => act("approve")} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"><Icon name="check" size={14} />Approve</button>
        <button disabled={busy} onClick={() => act("reject")} className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"><Icon name="x" size={14} />Reject</button>
      </div>
    </div>
  );
}

function Detail({ studentId, onBack, toast }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("progress");
  const [lockMod, setLockMod] = useState("");
  const [enrolData, setEnrolData] = useState(null);
  const [enrolSearch, setEnrolSearch] = useState("");
  const loadEnrol = () => api.studentEnrollments(studentId).then(setEnrolData);
  const [lockDays, setLockDays] = useState("");
  const [note, setNote] = useState("");
  const load = () => api.studentDetail(studentId).then(setD).catch((e) => toast && toast(e.message));
  useEffect(() => { load(); }, [studentId]);
  if (!d) return <div><button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 mb-4">← Back to students</button><div className="text-slate-400">Loading…</div></div>;

  const decide = async (action) => {
    if (action === "reject" && !note.trim()) return toast && toast("Add a note explaining the rejection.");
    try { await api.decideAssessment(studentId, { action, note }); toast && toast(action === "approve" ? "Assessment approved" : "Assessment rejected"); setNote(""); load(); }
    catch (e) { toast && toast(e.message); }
  };
  const approveCert = async () => {
    try { await api.approveCertificate(studentId); toast && toast("Certificate approved & issued"); load(); }
    catch (e) { toast && toast(e.message); }
  };
  const setProject = async (val) => {
    try { await api.setProjectRequirement(studentId, val); toast && toast("Project requirement updated"); load(); }
    catch (e) { toast && toast(e.message); }
  };
  const saveLock = async () => {
    try { await api.setStudentLock(studentId, lockMod ? Number(lockMod) : null, lockDays ? Number(lockDays) : null); toast && toast(lockMod && lockDays ? "Module lock set" : "Lock cleared"); load(); }
    catch (e) { toast && toast(e.message); }
  };
  const togglePause = async () => {
    try { await api.setStudentPause(studentId, !d.paused); toast && toast(d.paused ? "Course resumed" : "Course paused"); load(); }
    catch (e) { toast && toast(e.message); }
  };
  const fb = d.feedback || null;
  const statusColor = { Approved: "text-green-700 bg-green-50", Pending: "text-amber-700 bg-amber-50", Rejected: "text-red-700 bg-red-50" }[d.final_status] || "text-slate-500 bg-slate-100";

  return (
    <div className="pb-16">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 mb-4">← Back to students</button>
      <div className="flex items-center gap-3 mb-6"><Avatar name={d.full_name} size={48} /><div><h1 className="text-2xl font-bold text-slate-900">{d.full_name}</h1><p className="text-slate-500 text-sm">{d.course_name} · {d.email}</p></div><div className="ml-auto"><Badge>{d.status}</Badge></div></div>

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {[["progress", "Progress"], ["assessment", "Assessment"], ...(d.assignments_enabled ? [["assignments", "Assignments"]] : []), ["feedback", "Feedback"], ["details", "Details"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === k ? "border-current" : "border-transparent text-slate-400 hover:text-slate-600"}`} style={tab === k ? { color: BRAND } : {}}>{l}</button>
        ))}
      </div>

      {tab === "progress" && (
        <div className="space-y-3">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-700">Overall progress</span>
              <span className="text-sm font-semibold" style={{ color: BRAND }}>{d.progress}%</span>
            </div>
            <ProgressBar value={d.progress} />
          </Card>
          {(d.module_progress || []).map((mod) => {
            const allDone = mod.completed_count === mod.sub_count;
            const anyInProgress = mod.sub_modules.some((s) => s.status === "in_progress");
            const modStatus = allDone ? "completed" : anyInProgress ? "in_progress" : mod.sub_modules.every((s) => s.status === "locked" || s.status === "time_locked") ? "locked" : "partial";
            const modIcon = { completed: "check", in_progress: "play", locked: "lock", partial: "play" }[modStatus];
            const modColor = { completed: "text-emerald-600", in_progress: `text-[${BRAND}]`, locked: "text-slate-400", partial: "text-amber-500" }[modStatus];
            return (
              <Card key={mod.id} className="overflow-hidden">
                <div className="px-5 py-4 flex items-center gap-3 border-b border-slate-100">
                  <span className={modColor}><Icon name={modIcon} size={16} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{mod.title}</div>
                    <div className="text-xs text-slate-400">{mod.completed_count} / {mod.sub_count} lessons completed</div>
                  </div>
                  <div className="w-24"><ProgressBar value={mod.sub_count ? Math.round(mod.completed_count / mod.sub_count * 100) : 0} /></div>
                </div>
                <div className="divide-y divide-slate-50">
                  {mod.sub_modules.map((sub) => {
                    const icons = { completed: "check", in_progress: "play", locked: "lock", time_locked: "clock" };
                    const colors = { completed: "bg-emerald-100 text-emerald-600", in_progress: "bg-indigo-100 text-indigo-600", locked: "bg-slate-100 text-slate-400", time_locked: "bg-amber-100 text-amber-600" };
                    const labels = { completed: "Completed", in_progress: "In progress", locked: "Locked", time_locked: "Time-locked" };
                    return (
                      <div key={sub.id} className="flex items-center gap-3 px-5 py-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${colors[sub.status]}`}><Icon name={icons[sub.status]} size={13} /></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-700 truncate">{sub.title}</div>
                          <div className="text-xs text-slate-400 flex gap-3 mt-0.5">
                            {sub.video_completed && <span>Video ✓</span>}
                            {sub.quiz_passed && <span>Quiz ✓ ({sub.best_score}%)</span>}
                            {!sub.quiz_passed && sub.attempts > 0 && <span>Quiz: {sub.attempts} attempt{sub.attempts !== 1 ? "s" : ""} (best {sub.best_score}%)</span>}
                            {sub.unlock_date && <span>Unlocks {new Date(sub.unlock_date).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${colors[sub.status]}`}>{labels[sub.status]}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {tab === "assessment" && (<>
        <Card className="p-5 mb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-slate-700">Course access</div>
              <p className="text-xs text-slate-400 mt-0.5">{d.paused ? "Paused — the student cannot access any lessons." : "Active — the student can access the course."}</p>
            </div>
            <Btn variant={d.paused ? "primary" : "outline"} onClick={togglePause}>{d.paused ? "Resume course" : "Pause course"}</Btn>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-sm font-medium text-slate-700 mb-1">Custom module lock</div>
            <p className="text-xs text-slate-400 mb-3">Lock a module and everything after it for a number of days from the student's join date.</p>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-xs text-slate-500 mb-1">From module</label>
                <select value={lockMod} onChange={(e) => setLockMod(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm min-w-[220px]">
                  <option value="">— none —</option>
                  {(d.course_modules || []).map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Days</label>
                <input type="number" min="1" value={lockDays} onChange={(e) => setLockDays(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm w-24" />
              </div>
              <Btn onClick={saveLock}>Save</Btn>
              {(d.lock_from_module_id || d.lock_days) && <Btn variant="ghost" onClick={() => { setLockMod(""); setLockDays(""); api.setStudentLock(studentId, null, null).then(() => { toast && toast("Lock cleared"); load(); }); }}>Clear lock</Btn>}
            </div>
            {d.lock_from_module_id && d.lock_days
              ? <div className="text-xs text-slate-500 mt-2">Currently: <b>{(d.course_modules || []).find((m) => m.id === d.lock_from_module_id)?.title || "module"}</b> and after are locked for <b>{d.lock_days} days</b> from join.</div>
              : <div className="text-xs text-slate-400 mt-2">No custom lock set.</div>}
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-medium text-slate-700">Practical assessment</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>{d.final_status || "Not submitted"}</span>
          </div>
          {d.final_submitted ? (<>
            {d.final_submission && (d.final_submission.startsWith("/api/files/")
              ? <a href={api.fileUrl(d.final_submission)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"><Icon name="download" size={15} />View submission</a>
              : <a href={api.fileUrl(d.final_submission)} target="_blank" rel="noreferrer" className="text-sm underline" style={{ color: BRAND }}>{d.final_submission}</a>)}
            {d.final_status === "Rejected" && d.final_feedback && <div className="mt-3 text-sm text-red-600">Rejection note: {d.final_feedback}</div>}
            {d.final_status !== "Approved" && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <Field label="Note to student (required to reject)"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What needs to change?" /></Field>
                <div className="flex gap-2 mt-3">
                  <Btn onClick={() => decide("approve")}><Icon name="check" size={15} />Approve</Btn>
                  <Btn variant="danger" onClick={() => decide("reject")}>Reject</Btn>
                </div>
              </div>
            )}
            {d.final_status === "Approved" && <div className="mt-4 text-sm text-green-600 flex items-center gap-1"><Icon name="check" size={15} />Approved{d.certificate_issued ? " · certificate issued" : ""}</div>}
          </>) : <p className="text-sm text-slate-400">The student hasn't submitted their practical yet.</p>}
        </Card>
        <Card className="p-5 mt-4">
          <div className="text-sm font-medium text-slate-700 mb-1">Project submission requirement</div>
          <p className="text-xs text-slate-400 mb-3">Controls whether this student must get a practical approved before their certificate. Overrides the course default{d.course_project_default !== undefined ? ` (currently ${d.course_project_default ? "required" : "not required"})` : ""}.</p>
          <div className="flex items-center gap-2">
            {[["Inherit course default", null], ["Required", true], ["Not required", false]].map(([lbl, val]) => {
              const active = d.project_required === val;
              return <button key={lbl} onClick={() => setProject(val)} className="text-xs px-3 py-1.5 rounded-lg border" style={active ? { backgroundColor: BRAND, color: "#fff", borderColor: BRAND } : { borderColor: "#d6d3d1", color: "#57534e" }}>{lbl}</button>;
            })}
          </div>
          <div className="text-xs text-slate-500 mt-2">Effective for this student: <b>{d.project_required_effective ? "Project required" : "No project required"}</b></div>
        </Card>
        <Card className="p-5 mt-4">
          <div className="text-sm font-medium text-slate-700 mb-2">Certificate</div>
          {d.certificate_issued
            ? <div className="text-sm text-green-600 flex items-center gap-1"><Icon name="check" size={15} />Certificate approved & issued.</div>
            : d.certificate_requested
              ? <div className="space-y-3"><div className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">The student has requested their certificate.</div><Btn onClick={approveCert}><Icon name="check" size={15} />Approve &amp; issue certificate</Btn></div>
              : <p className="text-sm text-slate-400">No certificate request yet — the student can request it once all requirements are met.</p>}
        </Card>
      </>)}

      {tab === "assignments" && (
        <div className="space-y-3">
          {(d.assignments || []).length === 0 && <Card className="p-5"><p className="text-sm text-slate-400">No assignments submitted yet.</p></Card>}
          {(d.assignments || []).map((a) => {
            const statusColor = { Approved: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20", Pending: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20", Rejected: "bg-red-50 text-red-700 ring-1 ring-red-600/20" }[a.status] || "";
            return (
              <Card key={a.id} className="p-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div>
                    <span className="text-sm font-medium text-slate-700">{a.module_title}</span>
                    <span className="text-slate-300 mx-2">›</span>
                    <span className="text-sm text-slate-600">{a.sub_module_title}</span>
                  </div>
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${statusColor}`}>{a.status}</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <a href={api.fileUrl(a.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"><Icon name="download" size={14} />{a.filename}</a>
                  <span className="text-xs text-slate-400">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : ""}</span>
                </div>
                {a.feedback && <p className="text-xs text-slate-500 mt-2 bg-slate-50 rounded-lg p-2">{a.feedback}</p>}
                {a.status !== "Approved" && (
                  <AssignmentActions id={a.id} onDone={load} toast={toast} />
                )}
              </Card>
            );
          })}
        </div>
      )}

      {tab === "feedback" && (
        <Card className="p-5">
          {fb ? (<>
            {fb.ratings && Object.keys(fb.ratings).length > 0 && (
              <div className="mb-4"><div className="text-sm font-medium text-slate-700 mb-2">Ratings</div>
                <div className="space-y-1">{Object.entries(fb.ratings).map(([k, v]) => <div key={k} className="flex justify-between text-sm text-slate-600 border-b border-slate-100 py-1"><span>{k}</span><span>{v} / 5</span></div>)}</div>
              </div>
            )}
            <div className="text-sm font-medium text-slate-700 mb-1">Suggestions</div>
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{fb.suggestions || "—"}</p>
          </>) : <p className="text-sm text-slate-400">No feedback submitted yet.</p>}
        </Card>
      )}

      {tab === "details" && (<>
        <Card className="p-5 mb-4">
          <div className="text-sm font-medium text-slate-700 mb-1">Enrolled courses</div>
          <p className="text-xs text-slate-400 mb-3">Manage which courses this student has access to (same login credentials).</p>
          {!enrolData ? <Btn variant="ghost" onClick={loadEnrol}>Load courses</Btn> : (<>
            <div className="space-y-1 mb-3">
              {enrolData.enrolled.map((e) => (
                <div key={e.course_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                  <span className="text-sm text-slate-800">{e.course_name} <span className="text-xs text-slate-400">({e.status})</span></span>
                  {enrolData.enrolled.length > 1 && <button onClick={async () => { if (confirm(`Remove ${e.course_name}?`)) { await api.unenrollStudent(studentId, e.course_id); loadEnrol(); load(); toast("Removed from course"); }}} className="text-xs text-red-500 hover:text-red-700">Remove</button>}
                </div>
              ))}
            </div>
            {enrolData.available.length > 0 && (<>
              <Input placeholder="Search courses…" value={enrolSearch} onChange={(e) => setEnrolSearch(e.target.value)} className="mb-2" />
              <div className="max-h-40 overflow-y-auto space-y-1">
                {enrolData.available.filter((c) => c.name.toLowerCase().includes(enrolSearch.toLowerCase())).map((c) => (
                  <button key={c.id} onClick={async () => { try { await api.enrollStudent(studentId, c.id); loadEnrol(); load(); toast(`Enrolled in ${c.name}`); } catch (ex) { toast(ex.message); } }}
                    className="w-full flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 text-left">
                    <Icon name="plus" size={14} />{c.name}
                  </button>
                ))}
              </div>
            </>)}
          </>)}
        </Card>
    
        <Card className="p-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name"><Input defaultValue={d.full_name} /></Field>
            <Field label="Email"><Input defaultValue={d.email} /></Field>
            <Field label="Contact"><Input defaultValue={d.contact} /></Field>
            <Field label="Course"><Input defaultValue={d.course_name} /></Field>
            <Field label="Access ends"><Input defaultValue={new Date(d.expires_at).toLocaleDateString()} /></Field>
            {d.created_by_name && <Field label="Added by"><Input defaultValue={d.created_by_name} readOnly /></Field>}
          </div>
          <Card className="p-4 mt-5" style={{ backgroundColor: BRAND_TINT }}>
            <div className="text-sm font-medium text-slate-700 mb-2">Overall progress</div>
            <ProgressBar value={d.progress} />
            <div className="text-xs text-slate-500 mt-2">{d.progress}% complete</div>
          </Card>
        </Card>
      </>)}
    </div>
  );
}


export default function Students({ toast, focusStudent, onFocusHandled, can }) {
  const _can = can || (() => true);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", contact: "", course_id: "", duration_days: "", password: "" });
  const [created, setCreated] = useState(null);
  const [err, setErr] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("All");
  const [q, setQ] = useState("");
  const [statusFor, setStatusFor] = useState(null);
  const [detail, setDetail] = useSubView(null);
  useEffect(() => { if (focusStudent) { setDetail(focusStudent); onFocusHandled && onFocusHandled(); } }, [focusStudent]);

  const load = () => {
    api.students().then(setStudents).catch(() => {});
    api.courses().then((c) => { setCourses(c); if (c[0]) setForm((f) => ({ ...f, course_id: f.course_id || c[0].id })); });
  };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setErr(""); setCreated(null);
    try {
      const body = { full_name: form.full_name, email: form.email, contact: form.contact, course_id: Number(form.course_id) };
      if (form.duration_days) body.duration_days = Number(form.duration_days);
      if (form.password) body.password = form.password;
      const res = await api.createStudent(body);
      setCreated(res); setForm({ ...form, full_name: "", email: "", contact: "", duration_days: "", password: "" });
      load(); toast("Student created — share the password below");
    } catch (ex) { setErr(ex.message); }
  };
  const removeStudent = async (id, name) => {
    if (!confirm(`Delete student ${name}? This removes their login and progress.`)) return;
    try { await api.deleteUser(id); toast("Student deleted"); load(); } catch (e) { toast(e.message); }
  };
  const extend = async (id) => {
    const days = Number(prompt("Extend access by how many days?", "30"));
    if (!days) return;
    await api.extend(id, days); load(); toast(`Access extended by ${days} days`);
  };

  if (detail) return <Detail studentId={detail} toast={toast} onBack={() => { setDetail(null); load(); }} />;

  const rows = students
    .filter((s) => courseFilter === "all" || s.course_name === courseFilter)
    .filter((s) => statusFilter === "All" || s.status === statusFilter)
    .filter((s) => s.full_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHead title="Students" sub={`${students.length} enrolments · provision logins and manage access.`} />
      {_can("add_students") && <Card className="p-5 mb-6">
        <div className="font-semibold text-slate-800 mb-4">Add a student</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Full name"><Input value={form.full_name} onChange={set("full_name")} placeholder="Jane Doe" /></Field>
          <Field label="Email"><Input value={form.email} onChange={set("email")} placeholder="jane@example.com" /></Field>
          <Field label="Contact"><Input value={form.contact} onChange={set("contact")} placeholder="70340176.." /></Field>
          <Field label="Course"><Select value={form.course_id} onChange={set("course_id")}>{courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
          <Field label="Access duration (days)"><Input value={form.duration_days} onChange={set("duration_days")} placeholder="defaults to course setting" /></Field>
          <Field label="Password"><Input value={form.password} onChange={set("password")} placeholder="blank = auto-generate" /></Field>
          <div className="flex items-end"><Btn onClick={submit} className="w-full"><Icon name="plus" size={16} />Create login</Btn></div>
        </div>
        {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
        {created && (
          <div className="mt-4 rounded-lg p-4" style={{ backgroundColor: BRAND_SOFT }}>
            <div className="text-sm font-semibold" style={{ color: BRAND_DARK }}>Login created for {created.full_name}</div>
            <div className="text-sm text-slate-700 mt-1">Email: <b>{created.email}</b> · Temporary password: <b>{created.temp_password}</b></div>
            <div className="text-xs text-slate-500 mt-1">Share these with the student. Access expires {new Date(created.expires_at).toLocaleDateString()}.</div>
          </div>
        )}
      </Card>}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative w-60"><span className="absolute left-3 top-2.5 text-slate-400"><Icon name="search" size={15} /></span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200" /></div>
        <div className="w-56"><Select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}><option value="all">All courses</option>{courses.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</Select></div>
        <div className="flex gap-1">{["All", "Active", "Completed", "On Hold", "Discontinued"].map((f) => (
          <button key={f} onClick={() => setStatusFilter(f)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === f ? "text-slate-900" : "text-slate-400 hover:text-slate-600"}`} style={statusFilter === f ? { backgroundColor: BRAND_SOFT } : {}}>{f}</button>
        ))}</div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
            <th className="px-4 py-3 font-medium">Sl.</th><th className="px-4 py-3 font-medium">Student</th><th className="px-4 py-3 font-medium">Contact</th>
            <th className="px-4 py-3 font-medium">Course</th><th className="px-4 py-3 font-medium">Progress</th><th className="px-4 py-3 font-medium">Access ends</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {rows.map((s, i) => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                <td className="px-4 py-3"><div className="flex items-center gap-3"><Avatar name={s.full_name} size={32} /><div><div className="font-medium text-slate-900">{s.full_name}</div><div className="text-xs text-slate-400">{s.email}</div></div></div></td>
                <td className="px-4 py-3 text-slate-600">{s.contact}</td>
                <td className="px-4 py-3 text-slate-600">{s.course_name}</td>
                <td className="px-4 py-3"><div className="w-28"><ProgressBar value={s.progress} /><div className="text-[11px] text-slate-400 mt-1">{s.progress}%</div></div></td>
                <td className="px-4 py-3 text-slate-600">{new Date(s.expires_at).toLocaleDateString()}</td>
                <td className="px-4 py-3"><Badge>{s.is_active ? s.status : "Expired"}</Badge></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Btn variant="outline" onClick={() => extend(s.id)} className="py-1.5"><Icon name="clock" size={14} />Extend</Btn>
                    <RowMenu items={[
                      { label: "View", onClick: () => setDetail(s.id) },
                      { label: "Change status", onClick: () => setStatusFor(s) },
                      { label: "Edit", onClick: () => setDetail(s.id) },
                      ...(_can("delete_student") ? [{ label: "Delete", onClick: () => removeStudent(s.id, s.full_name) }] : []),
                    ]} />
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">No students match these filters.</td></tr>}
          </tbody>
        </table></div>
      </Card>

      <StatusModal student={statusFor} onClose={() => setStatusFor(null)} onSaved={load} />
    </div>
  );
}
